import { useEffect, useRef, useState } from "react";
import { PaperPlaneRight, PushPin, PushPinSlash, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { API, fmtErr, mediaUrl } from "../lib/api";
import MediaUpload from "./MediaUpload";

export default function EventChat({ eventId }) {
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [media, setMedia] = useState(null);
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const load = () => API.get(`/events/${eventId}/chat`)
    .then((r) => setData(r.data)).catch(() => {});

  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [eventId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [data?.messages?.length]);

  const send = async () => {
    if (sending || (!text.trim() && !media)) return;
    setSending(true);
    try {
      await API.post(`/events/${eventId}/chat`, {
        text: text.trim(), media_path: media?.path || null,
        media_type: media?.content_type || null,
      });
      setText("");
      setMedia(null);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
    setSending(false);
  };

  const remove = async (id) => {
    try {
      await API.delete(`/events/chat/${id}`);
      setData((d) => ({ ...d, messages: d.messages.filter((m) => m.id !== id) }));
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const togglePin = async (id) => {
    try {
      await API.post(`/events/chat/${id}/pin`);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div className="card-neon mt-4 overflow-hidden" data-testid="event-chat">
      <div className="border-b border-white/10 px-4 py-3">
        <span className="flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-neon-gold">
          <span className="h-2 w-2 animate-pulse rounded-full bg-neon-green" /> Live Chat
        </span>
      </div>
      <div className="max-h-72 space-y-3 overflow-y-auto px-4 py-3" data-testid="chat-messages">
        {data?.pinned_message && (
          <div className="flex items-start gap-2 rounded-xl border border-neon-gold/50 bg-neon-gold/10 px-3 py-2.5" data-testid="chat-pinned">
            <PushPin size={14} weight="fill" className="mt-0.5 shrink-0 text-neon-gold" />
            <div className="min-w-0 flex-1">
              <div className="text-[9px] font-black uppercase tracking-widest text-neon-gold">Pinned by the venue</div>
              {data.pinned_message.text && <p className="mt-0.5 break-words text-sm font-bold text-zinc-100">{data.pinned_message.text}</p>}
              {data.pinned_message.media_path && data.pinned_message.media_type?.startsWith("image/") && (
                <img src={mediaUrl(data.pinned_message.media_path)} alt="" className="mt-1.5 max-h-32 rounded-lg border border-white/10 object-cover" />
              )}
              {data.pinned_message.media_path && data.pinned_message.media_type?.startsWith("video/") && (
                <video src={mediaUrl(data.pinned_message.media_path)} controls playsInline className="mt-1.5 max-h-32 w-full rounded-lg border border-white/10" />
              )}
            </div>
            {data.can_pin && (
              <button data-testid="chat-unpin-btn" onClick={() => togglePin(data.pinned_message.id)}
                className="shrink-0 text-zinc-500 hover:text-neon-gold"><PushPinSlash size={14} /></button>
            )}
          </div>
        )}
        {data === null ? (
          <p className="py-4 text-center text-xs text-zinc-600">Loading chat…</p>
        ) : data.messages.length === 0 ? (
          <p className="py-4 text-center text-xs text-zinc-600">No messages yet — be the first to show what's going on.</p>
        ) : data.messages.map((m) => (
          <div key={m.id} className="group flex items-start gap-2.5" data-testid={`chat-msg-${m.id}`}>
            <div className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full border border-neon-gold/40 bg-neon-gold/10 text-xs font-black text-neon-gold">
              {m.avatar_url ? <img src={mediaUrl(m.avatar_url)} alt="" className="h-full w-full object-cover" /> : m.name?.[0]?.toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline gap-2">
                <span className={`text-[11px] font-black ${m.role === "promoter" ? "text-neon-violet" : m.role === "venue" ? "text-neon-gold" : "text-zinc-300"}`}>
                  {m.name}{m.role === "promoter" ? " · PROMOTER" : m.role === "venue" ? " · VENUE" : ""}
                </span>
                <span className="text-[9px] text-zinc-600">{new Date(m.created_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</span>
                {m.pinned && <PushPin size={11} weight="fill" className="text-neon-gold" />}
                {data.can_pin && !m.pinned && (
                  <button data-testid={`chat-pin-${m.id}`} onClick={() => togglePin(m.id)}
                    className="text-zinc-700 opacity-0 transition-opacity group-hover:opacity-100 hover:text-neon-gold">
                    <PushPin size={12} />
                  </button>
                )}
                {(m.own || data.can_moderate) && (
                  <button data-testid={`chat-delete-${m.id}`} onClick={() => remove(m.id)}
                    className="text-zinc-700 opacity-0 transition-opacity group-hover:opacity-100 hover:text-red-400">
                    <Trash size={12} />
                  </button>
                )}
              </div>
              {m.text && <p className="mt-0.5 break-words text-sm text-zinc-200">{m.text}</p>}
              {m.media_path && m.media_type?.startsWith("image/") && (
                <img src={mediaUrl(m.media_path)} alt="" data-testid={`chat-img-${m.id}`}
                  className="mt-1.5 max-h-48 rounded-xl border border-white/10 object-cover" />
              )}
              {m.media_path && m.media_type?.startsWith("video/") && (
                <video src={mediaUrl(m.media_path)} controls playsInline data-testid={`chat-video-${m.id}`}
                  className="mt-1.5 max-h-48 w-full rounded-xl border border-white/10" />
              )}
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>
      {data && (
        <div className="border-t border-white/10 p-3">
          {data.can_post ? (
            <>
              {media && (
                <div className="mb-2 flex items-center gap-2 text-[11px] text-neon-gold">
                  <span className="truncate">Attached: {media.content_type?.startsWith("video/") ? "video" : "photo"}</span>
                  <button data-testid="chat-media-remove" onClick={() => setMedia(null)} className="text-zinc-500"><Trash size={12} /></button>
                </div>
              )}
              <div className="flex items-center gap-2">
                <MediaUpload clearAfterUpload label="+" onUploaded={setMedia} />
                <input data-testid="chat-input" value={text} maxLength={500}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder="Share what's going on…"
                  className="flex-1 rounded-full border border-white/10 bg-black px-4 py-2 text-sm outline-none focus:border-neon-gold" />
                <button data-testid="chat-send-btn" onClick={send} disabled={sending || (!text.trim() && !media)}
                  className="rounded-full bg-neon-gold p-2.5 text-black disabled:opacity-40">
                  <PaperPlaneRight size={16} weight="fill" />
                </button>
              </div>
            </>
          ) : (
            <p className="text-center text-[11px] font-bold uppercase tracking-wider text-zinc-600" data-testid="chat-readonly-note">
              Check in at the event to join the chat
            </p>
          )}
        </div>
      )}
    </div>
  );
}
